import { AnimatePresence, motion, type Transition } from "motion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DYNAMIC_ISLAND } from "../../../config/dynamicIsland.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import {
    getDynamicIslandVariant,
    type DynamicIslandHostContext,
} from "../dynamicIsland.registry";
import { type DynamicIslandKind, type DynamicIslandPayload } from "../dynamicIsland.types";
import DynamicIslandVariantShell from "../layout/DynamicIslandVariantShell";
import {
    DYNAMIC_ISLAND_INSTANT_TRANSITION,
    type DynamicIslandAnimationPhase,
    type DynamicIslandShellMotionTargets,
} from "./dynamicIslandAnimation.types";
import { triggerDynamicIslandHaptic } from "./dynamicIslandHaptics.tools";
import { resolveDynamicIslandMotion } from "./resolveDynamicIslandMotion";
import { useDynamicIslandPresence } from "./useDynamicIslandPresence.hook";

/** Shared LayoutGroup id between island shell and FAB row. */
export const DYNAMIC_ISLAND_LAYOUT_GROUP_ID = "dynamic-island";

interface DynamicIslandPresenceShellProps {

    /** Live orchestrator payload. */
    "payload": DynamicIslandPayload | undefined;

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Host capabilities for variant-owned interaction handlers. */
    "hostContext": DynamicIslandHostContext;

    /** Notified when FAB row should reserve center column space. */
    "onLayoutOccupancyChange"?: ( occupied: boolean ) => void;

    /** Optional phase callback for diagnostics. */
    "onPhaseChange"?: ( phase: DynamicIslandAnimationPhase ) => void;
}

/**
 * Builds Motion targets for shell enter and exit choreography.
 *
 * @param kind Active payload kind.
 * @param isKindSwitchEnter Whether this mount follows a different kind.
 * @param useDirectionalExit Whether exit should slide (kind switch or post-open close).
 * @param reducedMotion Whether animations should be instant.
 * @returns Initial, animate, and exit targets.
 */
function buildShellVariants(
    kind: DynamicIslandKind,
    isKindSwitchEnter: boolean,
    useDirectionalExit: boolean,
    reducedMotion: boolean,
): DynamicIslandShellMotionTargets {
    const motionConfig = resolveDynamicIslandMotion( kind );
    const { shell } = motionConfig;

    if ( reducedMotion || !DYNAMIC_ISLAND.animations.enabled ) {
        return {
            "initial": { "opacity": 1 },
            "animate": { "opacity": 1 },
            "exit":    { "opacity": 1 },
        };
    }

    const directionalExit = {
        "opacity":    shell.kindSwitch.outgoing.opacity[1],
        "transition": KIND_SWITCH_EXIT_TRANSITION,
    };

    if ( isKindSwitchEnter ) {
        // Opacity-only crossfade — x/scale caused off-center flashes with stacked pills.
        return {
            "initial": { "opacity": 0 },
            "animate": {
                "opacity":    1,
                "transition": KIND_SWITCH_ENTER_TRANSITION,
            },
            "exit": {
                "opacity":    0,
                "transition": KIND_SWITCH_EXIT_TRANSITION,
            },
        };
    }

    return {
        "initial": {
            "opacity": shell.enter.opacity[0],
            "scaleX":  shell.enter.scaleX[0],
            "scaleY":  shell.enter.scaleY[0],
        },
        "animate": {
            "opacity": shell.enter.opacity[1],
            "scaleX":  shell.enter.scaleX[1],
            "scaleY":  shell.enter.scaleY[1],
        },
        "exit": useDirectionalExit
            ? directionalExit
            : {
                "opacity": shell.exit.opacity[1],
                "scaleX":  shell.exit.scaleX[1],
                "scaleY":  shell.exit.scaleY[1],
            },
    };
}

/** Kind-switch crossfade duration (seconds). */
const KIND_SWITCH_CROSSFADE_DURATION_S = 0.22;

/**
 * Complementary easing: enter ramps up quickly, exit stays opaque longer.
 * Same easeOut on both caused a mid-fade dip (visible blink).
 */
const KIND_SWITCH_ENTER_TRANSITION = {
    "duration": KIND_SWITCH_CROSSFADE_DURATION_S,
    "ease":     "easeOut",
} as const;

const KIND_SWITCH_EXIT_TRANSITION = {
    "duration": KIND_SWITCH_CROSSFADE_DURATION_S,
    "ease":     "easeIn",
} as const;

interface DynamicIslandMotionPillProps {
    "activePayload":      DynamicIslandPayload;
    "enterAsKindSwitch":  boolean;
    "hostContext":        DynamicIslandHostContext;
    "isDarkUi":           boolean;
    "onEnterComplete":    () => void;
    "reducedMotion":      boolean;
    "transition":         Transition;
    "useDirectionalExit": boolean;
}

/**
 * Single animated island pill instance (one per payload kind key).
 *
 * @param props Pill props.
 * @returns Animated shell around variant content.
 */
const DynamicIslandMotionPill: React.FC<DynamicIslandMotionPillProps> = ( {
    activePayload,
    enterAsKindSwitch,
    hostContext,
    isDarkUi,
    onEnterComplete,
    reducedMotion,
    transition,
    useDirectionalExit,
} ) => {
    const hasReportedEnterRef = useRef<boolean>( false );

    /*
     * Freeze kind-switch enter mode at mount. Updating previousKind on enter
     * complete used to flip this prop and re-target the shell mid-crossfade.
     */
    const [ lockedEnterAsKindSwitch ] = useState( enterAsKindSwitch );

    const shellTargets = useMemo(
        () => buildShellVariants(
            activePayload.kind,
            lockedEnterAsKindSwitch,
            useDirectionalExit,
            reducedMotion,
        ),
        [ activePayload.kind, lockedEnterAsKindSwitch, reducedMotion, useDirectionalExit ],
    );

    const usesKindSwitchCrossfade = lockedEnterAsKindSwitch || useDirectionalExit;

    return (
        <motion.div
            animate={shellTargets.animate}
            className="pointer-events-none absolute inset-x-0 top-0 w-full min-w-0"
            exit={shellTargets.exit}
            initial={shellTargets.initial}
            onAnimationComplete={( definition ): void => {
                // Motion also fires on exit — spurious enter callbacks caused flicker.
                if ( definition === "exit" || hasReportedEnterRef.current ) {
                    return;
                }

                hasReportedEnterRef.current = true;
                onEnterComplete();
            }}
            style={{ "zIndex": lockedEnterAsKindSwitch ? 2 : 1 }}
            transition={usesKindSwitchCrossfade ? undefined : transition}
        >
            <DynamicIslandIslandBody
                activePayload={activePayload}
                enterAsKindSwitch={lockedEnterAsKindSwitch}
                hostContext={hostContext}
                isDarkUi={isDarkUi}
                reducedMotion={reducedMotion}
            />
        </motion.div>
    );
};

/**
 * Animated presence shell for the Dynamic Island pill.
 *
 * @param props Component props.
 * @returns Animated island shell or null when disabled and empty.
 */
const DynamicIslandPresenceShell: React.FC<DynamicIslandPresenceShellProps> = ( {
    payload,
    isDarkUi,
    hostContext,
    onLayoutOccupancyChange,
    onPhaseChange,
} ) => {
    const reducedMotion = useReducedMotion();
    const [ previousKind, setPreviousKind ] = useState<DynamicIslandKind | undefined>( undefined );
    const {
        phase,
        handleExitComplete,
        handleEnterComplete,
    } = useDynamicIslandPresence( { payload } );

    const animationsEnabled = DYNAMIC_ISLAND.enabled && DYNAMIC_ISLAND.animations.enabled && !reducedMotion;

    const transition = useMemo( (): Transition => {
        if ( !animationsEnabled || payload === undefined ) {
            return DYNAMIC_ISLAND_INSTANT_TRANSITION;
        }

        return resolveDynamicIslandMotion( payload.kind ).spring;
    }, [ animationsEnabled, payload ] );

    const enterAsKindSwitch = previousKind !== undefined &&
      payload !== undefined &&
      previousKind !== payload.kind;

    useEffect( () => {
        const occupied = phase !== "idle";
        onLayoutOccupancyChange?.( occupied );
        onPhaseChange?.( phase );
    }, [ onLayoutOccupancyChange, onPhaseChange, phase ] );

    useEffect( () => {
        if ( phase === "entering" && payload !== undefined ) {
            void triggerDynamicIslandHaptic( "open", reducedMotion );
        }
    }, [ payload, phase, reducedMotion ] );

    const handlePillEnterComplete = (): void => {
        if ( payload !== undefined ) {
            setPreviousKind( payload.kind );
        }

        handleEnterComplete();
    };

    if ( !DYNAMIC_ISLAND.enabled ) {
        return null;
    }

    if ( !animationsEnabled ) {
        if ( payload === undefined ) {
            return null;
        }

        const registration = getDynamicIslandVariant( payload.kind );
        if ( registration === undefined ) {
            return null;
        }

        const ContentComponent = registration.ContentComponent;

        return (
            <div className="w-full min-w-0">
                <DynamicIslandVariantShell
                    isDarkUi={isDarkUi}
                    shell={registration.shell}
                >
                    <ContentComponent
                        hostContext={hostContext}
                        isDarkUi={isDarkUi}
                        payload={payload}
                    />
                </DynamicIslandVariantShell>
            </div>
        );
    }

    return (

        /*
         * Pills are absolutely stacked so sync crossfade never widens the FAB slot
         * (popLayout + LayoutGroup caused layout thrash and visible flicker).
         */
        <div
            className="relative isolate w-full min-w-0"
            style={{ "height": `${ DYNAMIC_ISLAND.heightPx }px` }}
        >
            <AnimatePresence
                mode="sync"
                onExitComplete={(): void => {
                    // Kind switches also exit the previous pill — only finalize on full close.
                    if ( payload !== undefined ) {
                        return;
                    }

                    setPreviousKind( undefined );
                    void triggerDynamicIslandHaptic( "close", reducedMotion );
                    handleExitComplete();
                }}
            >
                {payload !== undefined && (
                    <DynamicIslandMotionPill
                        activePayload={payload}
                        enterAsKindSwitch={enterAsKindSwitch}
                        hostContext={hostContext}
                        isDarkUi={isDarkUi}
                        key={payload.kind}
                        onEnterComplete={handlePillEnterComplete}
                        reducedMotion={reducedMotion}
                        transition={transition}
                        useDirectionalExit={previousKind !== undefined}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

interface DynamicIslandIslandBodyProps {
    "activePayload":     DynamicIslandPayload;
    "enterAsKindSwitch": boolean;
    "isDarkUi":          boolean;
    "hostContext":       DynamicIslandHostContext;
    "reducedMotion":     boolean;
}

/**
 * Renders variant content with optional per-variant motion wrapper.
 *
 * @param props Body props.
 * @returns Variant tree.
 */
const DynamicIslandIslandBody: React.FC<DynamicIslandIslandBodyProps> = ( {
    activePayload,
    enterAsKindSwitch,
    isDarkUi,
    hostContext,
    reducedMotion,
} ) => {
    const registration = getDynamicIslandVariant( activePayload.kind );
    if ( registration === undefined ) {
        return null;
    }

    const ContentComponent = registration.ContentComponent;
    const animationRegistration = registration.animation;
    const animationKey = animationRegistration?.getPayloadAnimationKey( activePayload ) ?? activePayload.kind;
    const MotionWrapper = animationRegistration?.ContentMotionWrapper;

    const content = (
        <ContentComponent
            hostContext={hostContext}
            isDarkUi={isDarkUi}
            payload={activePayload}
        />
    );

    const wrappedContent = MotionWrapper !== undefined
        ? (
            <MotionWrapper
                animationKey={animationKey}
                payload={activePayload}
                reducedMotion={reducedMotion}
                skipEnterAnimation={enterAsKindSwitch}
            >
                {content}
            </MotionWrapper>
        )
        : content;

    return (
        <DynamicIslandVariantShell
            isDarkUi={isDarkUi}
            shell={registration.shell}
        >
            {wrappedContent}
        </DynamicIslandVariantShell>
    );
};

export default DynamicIslandPresenceShell;
